import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center px-4">
          <div className="w-full max-w-md animate-pop-in rounded-2xl border border-line-strong bg-surface p-8 text-center shadow-pop">
            <div className="mx-auto mb-5 grid size-12 place-items-center rounded-xl border border-danger/25 bg-danger/10 text-danger">
              <AlertTriangle className="size-6" />
            </div>
            <h2 className="text-lg font-semibold tracking-tight text-fg">Something went wrong</h2>
            <p className="mt-1.5 text-sm text-fg-muted">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <Button onClick={this.handleRetry} className="mt-6">
              <RefreshCw />
              Try again
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
